import { useState } from 'react'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { InstanceList } from './InstanceList'
import { InstanceDetail } from './InstanceDetail'
import { ConnectionDialog } from './ConnectionDialog'
import { ClassTree } from '@/components/browser/ClassTree'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import { useConnectionStore } from '@/stores/connectionStore'
import { useInstancesOfClass, useInstanceCount, useDeleteInstance } from '@/hooks/useInstances'
import type { OntologyClass, OntologyDomain, OntologyInstance } from '@/types/ontology'
import { Plus, RefreshCw, Unplug, Database } from 'lucide-react'

interface InstanceBrowserProps {
  filterDomain?: OntologyDomain | null
}

export function InstanceBrowser({ filterDomain }: InstanceBrowserProps) {
  const { isConnected, disconnect } = useConnectionStore()
  const [selectedClass, setSelectedClass] = useState<OntologyClass | null>(null)
  const [selectedInstance, setSelectedInstance] = useState<OntologyInstance | null>(null)

  const {
    data: instances = [],
    isLoading: instancesLoading,
    error: instancesError,
    refetch,
  } = useInstancesOfClass(selectedClass?.uri ?? null)

  const { data: instanceCount = 0 } = useInstanceCount(selectedClass?.uri ?? null)
  const deleteInstance = useDeleteInstance()

  const handleNavigate = (uri: string) => {
    // Find the instance in our current list or create a temporary one
    const existing = instances.find((i) => i.uri === uri)
    if (existing) {
      setSelectedInstance(existing)
    } else {
      // Create a temporary instance object for navigation
      setSelectedInstance({
        uri,
        type: '',
        typeLabel: 'Unknown',
        domain: 'solution',
        label: uri.split('#').pop() || uri,
        properties: {},
      })
    }
  }

  const handleDelete = async (uri: string) => {
    if (confirm('Are you sure you want to delete this instance?')) {
      await deleteInstance.mutateAsync(uri)
      setSelectedInstance(null)
    }
  }

  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <ConnectionDialog />
      </div>
    )
  }

  return (
    <div className="h-full flex gap-4">
      {/* Left Panel - Class Selector */}
      <div className="w-72 flex-shrink-0 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-text">Select Class</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={disconnect}
            className="text-text-muted hover:text-solution"
          >
            <Unplug className="w-4 h-4 mr-1" />
            Disconnect
          </Button>
        </div>
        <div className="flex-1 overflow-auto border border-border rounded-lg bg-surface p-2">
          <ClassTree
            classes={ALL_CLASSES}
            selectedClass={selectedClass}
            onSelectClass={setSelectedClass}
            filterDomain={filterDomain}
          />
        </div>
      </div>

      {/* Middle Panel - Instance List */}
      <div className="w-80 flex-shrink-0 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-text">Instances</h3>
            {selectedClass && instanceCount > 0 && (
              <Badge variant="outline">{instanceCount}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={!selectedClass}
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={!selectedClass}
              title="Create Instance"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto border border-border rounded-lg bg-surface p-2">
          {selectedClass ? (
            <InstanceList
              instances={instances}
              selectedInstance={selectedInstance}
              onSelectInstance={setSelectedInstance}
              loading={instancesLoading}
              error={instancesError?.message ?? null}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-text-muted text-sm text-center p-4">
              <div>
                <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Select a class to browse instances</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Instance Detail */}
      <div className="flex-1 overflow-auto">
        {selectedInstance ? (
          <InstanceDetail
            instanceUri={selectedInstance.uri}
            onNavigate={handleNavigate}
            onDelete={handleDelete}
          />
        ) : (
          <Card className="h-full flex items-center justify-center">
            <CardHeader className="text-center">
              <CardTitle className="text-text-muted">Select an Instance</CardTitle>
              <CardDescription>
                Choose an instance from the list to view its properties and relationships.
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
    </div>
  )
}
